<svelte:options customElement="cv-placeholder-input" />

<script lang="ts">
  import { activeStateStore } from '$lib/stores/active-state-store.svelte';
  import { placeholderRegistryStore } from '$features/placeholder/stores/placeholder-registry-store.svelte';
  import IconPencil from '$lib/app/icons/IconPencil.svelte';

  type Layout = 'inline' | 'stacked' | 'horizontal' | 'card';
  type Appearance = 'outline' | 'underline' | 'ghost';

  let {
    name,
    label,
    hint,
    layout = 'inline',
    appearance = 'outline',
    width,
  } = $props<{
    name: string;
    label?: string;
    hint?: string;
    layout?: Layout;
    appearance?: Appearance;
    width?: string;
  }>();

  let effectiveLayout = $derived(layout);

  let value = $derived(activeStateStore.state.placeholders?.[name] ?? '');

  let effectiveLabel = $derived.by(() => {
    if (label) return label;
    const def = placeholderRegistryStore.get(name);
    if (!def) return name;

    // For visible label layouts, try settingsLabel
    if (effectiveLayout !== 'inline' && def.settingsLabel) return def.settingsLabel;

    // Fallback
    return def.settingsLabel || name;
  });

  let effectiveHint = $derived.by(() => {
    if (hint) return hint;
    const def = placeholderRegistryStore.get(name);
    return def?.settingsHint || '';
  });

  let effectiveDescription = $derived(placeholderRegistryStore.get(name)?.description ?? '');

  let sanitizedId = $derived(name.replace(/[^a-zA-Z0-9_-]/g, '_'));

  function handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    activeStateStore.setPlaceholder(name, target.value);
  }

  let inputSize = $derived.by(() => {
    if (effectiveLayout !== 'inline' || width !== 'auto-grow') return undefined;

    const len = (value || effectiveHint).length;
    return Math.max(len, 4);
  });
</script>

<div
  class="cv-input-wrapper {effectiveLayout}"
  style:--cv-input-width={width === 'auto-grow' ? 'auto' : width}
>
  {#if effectiveLayout === 'card' && effectiveLabel}
    <div class="label-group">
      <label class="placeholder-label" for="cv-input-{sanitizedId}">{effectiveLabel}</label>
      {#if effectiveDescription}
        <p class="placeholder-description">{effectiveDescription}</p>
      {/if}
    </div>
  {:else if effectiveLayout !== 'inline' && effectiveLabel}
    <label class="placeholder-label" for="cv-input-{sanitizedId}">{effectiveLabel}</label>
  {/if}
  <div class="input-container">
    <input
      id="cv-input-{sanitizedId}"
      class="placeholder-input {appearance}"
      type="text"
      placeholder={effectiveHint}
      {value}
      oninput={handleInput}
      aria-label={effectiveLayout === 'inline' ? effectiveLabel : undefined}
      size={inputSize}
    />
    <span class="edit-icon" aria-hidden="true"><IconPencil /></span>
  </div>
</div>

<style>
  :host {
    display: inline-block;
    width: auto;
    margin: 0 0.25rem; /* Add breathing room for inline text */
  }

  /* Host display overrides based on layout */
  :host([layout='stacked']),
  :host([layout='horizontal']),
  :host([layout='card']) {
    display: block;
    width: 100%;
    margin: 0 0 0.5rem 0; /* Reset margins for block layouts */
  }

  /* Wrapper Grid/Flex Layouts */
  .cv-input-wrapper {
    display: flex;
    width: 100%;
    box-sizing: border-box;
  }

  /* INLINE */
  .cv-input-wrapper.inline {
    display: inline-block;
    width: auto;
  }

  /* STACKED */
  .cv-input-wrapper.stacked {
    flex-direction: column;
    gap: 0.25rem;
  }

  /* HORIZONTAL */
  .cv-input-wrapper.horizontal {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }

  /* CARD */
  .cv-input-wrapper.card {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    gap: 0.75rem;
    background: var(--cv-bg);
    border: 1px solid var(--cv-border);
    border-radius: var(--cv-card-radius, 0.5rem);
    transition: background 0.15s ease;
  }

  .label-group {
    display: flex;
    flex-direction: column;
  }

  .card .label-group {
    flex: 1;
    min-width: 0;
  }

  .card .placeholder-label {
    font-size: 0.875rem;
    font-weight: 500;
    white-space: normal;
  }

  .placeholder-description {
    font-size: 0.75rem;
    color: var(--cv-text-secondary);
    margin: 0.125rem 0 0 0;
    line-height: 1.4;
  }

  .card .placeholder-input {
    width: 100%;
  }

  /* Label Styles */
  .placeholder-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--cv-text, #333);
    white-space: nowrap;
  }
  .stacked .placeholder-label {
    margin-bottom: 2px;
    width: 100%; /* Ensure label context is full width */
    text-align: left; /* Reset text align */
  }

  /* Input Styles */
  .placeholder-input {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--cv-input-border, rgba(0, 0, 0, 0.1));
    border-radius: 0.375rem;
    font-size: 0.9rem;
    transition: all 0.2s;
    background: var(--cv-input-bg, white);
    color: var(--cv-text, #333);
    box-sizing: border-box;
    width: 100%;
  }

  .stacked .placeholder-input {
    width: 100%;
  }

  .inline .placeholder-input {
    width: var(--cv-input-width, auto);
    padding: 0.3rem 1.35rem 0.3rem 0.5rem;
    display: inline-block;
    text-align: center;
  }

  /* Input container — positions the pencil icon */
  .input-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  .inline .input-container {
    display: inline-flex;
  }

  .stacked .input-container {
    width: 100%;
  }

  .horizontal .input-container {
    flex: 1;
  }

  .card .input-container {
    flex: 1;
    min-width: 6rem;
  }

  .edit-icon {
    position: absolute;
    right: 0.3rem;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
    color: var(--cv-text-secondary, rgba(0, 0, 0, 0.5));
    opacity: 0.7;
  }

  .edit-icon :global(svg) {
    width: 0.75rem;
    height: 0.75rem;
  }

  /* More breathing room for block layouts */
  .stacked .edit-icon,
  .horizontal .edit-icon,
  .card .edit-icon {
    right: 0.5rem;
  }

  .horizontal .placeholder-input {
    width: var(--cv-input-width, auto);
    flex: 1;
  }

  /* APPEARANCES */

  /* Outline (Default) - handled by base styles above */

  /* Underline */
  .placeholder-input.underline {
    border: none;
    border-bottom: 1px solid var(--cv-input-border, rgba(0, 0, 0, 0.2));
    border-radius: 0;
    background: transparent;
    padding-left: 0;
    padding-right: 0;
  }
  .placeholder-input.underline:focus {
    box-shadow: none;
    border-bottom-color: var(--cv-primary, #3e84f4);
  }

  /* Ghost */
  .placeholder-input.ghost {
    border-color: transparent;
    background: transparent;
  }
  .placeholder-input.ghost:hover {
    background: var(--cv-input-bg-hover, rgba(0, 0, 0, 0.05));
  }
  .placeholder-input.ghost:focus {
    background: var(--cv-input-bg, white);
    border-color: var(--cv-primary, #3e84f4);
    box-shadow: 0 0 0 2px var(--cv-focus-ring, rgba(62, 132, 244, 0.2));
  }

  /* Focus states for standard inputs */
  .placeholder-input:not(.underline):focus {
    outline: none;
    border-color: var(--cv-primary, #3e84f4);
    box-shadow: 0 0 0 2px var(--cv-focus-ring, rgba(62, 132, 244, 0.2));
  }

  /* Icon padding for block layouts — after appearance rules so underline can't override */
  .stacked .placeholder-input,
  .horizontal .placeholder-input,
  .card .placeholder-input {
    padding-right: 1.6rem;
  }
</style>
